import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthOptional } from "@/contexts/AuthContext";
import { requireSupabase } from "@/lib/supabase";
import { clearKbDevRescopeIntent, readKbDevRescopeIntent, type KbDevRescopeIntentV1 } from "@/lib/kbDevRescopeIntent";
import { DevPanelBugReportsSection } from "./DevPanelBugReportsSection";
import { DevPanelKbRescopeSection } from "./DevPanelKbRescopeSection";
import { DevPanelActionableNotesSection } from "./DevPanelActionableNotesSection";
import { DevPanelActivityDockPeekStatsSection } from "./DevPanelActivityDockPeekStatsSection";
import { DevPanelFireflyLabSection } from "./DevPanelFireflyLabSection";
import { DevPanelFireflyTargetHighlightSection } from "./DevPanelFireflyTargetHighlightSection";
import { DevActivityForensicsPanel } from "./DevActivityForensicsPanel";
import { DevInboundReceiptsPanel } from "./DevInboundReceiptsPanel";
import { DevPanelElevateBugModal, type ElevateModalSubmitArgs } from "./DevPanelElevateBugModal";
import { type BugReport, type OrgPickRow } from "./devPanelTypes";
import { apiFetch, buildReportLookups, isUuid } from "./devPanelShared";

export function DevPanel() {
  const auth = useAuthOptional();
  const realPlatformOrgListAccess = auth?.status === "ready" && auth.realHasRole("platform_admin", "platform_dev");
  // platform_owner + platform_dev can elevate user bug reports into the
  // dev-sync queue. Trust tier of the resulting note follows the elevator's
  // roles, so platform_dev elevations still go through owner review.
  const canElevateBugs = auth?.status === "ready" && auth.realHasRole("platform_owner", "platform_dev");
  const [reports, setReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState("unresolved");
  const [filterMode, setFilterMode] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [filterOrganizationId, setFilterOrganizationId] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState("");
  const [editPriority, setEditPriority] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editResolutionSummary, setEditResolutionSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [elevatingReport, setElevatingReport] = useState<BugReport | null>(null);
  const [elevateBusy, setElevateBusy] = useState(false);
  const [elevateError, setElevateError] = useState<string | null>(null);
  const [kbRescopeCollapsed, setKbRescopeCollapsed] = useState(true);
  const [kbRescopeSlug, setKbRescopeSlug] = useState("");
  const [kbRescopeTargetScope, setKbRescopeTargetScope] = useState<"org" | "manufacturer" | "app">("org");
  const [kbRescopeTargetOrgId, setKbRescopeTargetOrgId] = useState("");
  const [kbRescopeUuidOverride, setKbRescopeUuidOverride] = useState("");
  const [kbRescopeOrgs, setKbRescopeOrgs] = useState<OrgPickRow[]>([]);
  const [kbRescopeOrgsLoading, setKbRescopeOrgsLoading] = useState(false);
  const [kbRescopeOrgsError, setKbRescopeOrgsError] = useState<string | null>(null);
  const [kbRescopeIntent, setKbRescopeIntent] = useState<KbDevRescopeIntentV1 | null>(null);
  const [kbRescopeReviewOpen, setKbRescopeReviewOpen] = useState(false);
  const [kbRescopeBusy, setKbRescopeBusy] = useState(false);
  const [kbRescopeMsg, setKbRescopeMsg] = useState<string | null>(null);
  const [kbRescopeDetail, setKbRescopeDetail] = useState<string | null>(null);
  const sb = useMemo(() => { try { return requireSupabase(); } catch { return null; } }, []);
  const lastKbIntentOpenedAtRef = useRef<string | null>(null);
  // Stable identity across DevPanel re-renders: the access token only changes
  // when the session itself changes, so child sections (which key effects on
  // `getToken`) don't re-fire their loaders every time an unrelated piece of
  // state in this very-stateful panel updates. Fixes the dev-sync-queue
  // empty-state flicker (dev-sync note 52448a83).
  const sessionAccessToken = (auth?.session as any)?.access_token ?? null;
  const getToken = useCallback(
    async () => sessionAccessToken as string | null,
    [sessionAccessToken],
  );

  async function loadReports() {
    const token = await getToken();
    if (!token) return;
    setLoading(true); setError(null);
    const params = new URLSearchParams();
    if (filterStatus) params.set("status", filterStatus);
    if (filterMode) params.set("mode", filterMode);
    if (filterPriority) params.set("priority", filterPriority);
    if (filterUserId) params.set("userId", filterUserId);
    if (filterOrganizationId) params.set("organizationId", filterOrganizationId);
    if (filterStartDate) params.set("startDate", filterStartDate);
    if (filterEndDate) params.set("endDate", filterEndDate);
    const result = await apiFetch<{ reports: BugReport[] }>(`/api/bugs/all?${params.toString()}`, "POST", token);
    setLoading(false);
    if (!result.ok) { setError(result.error); return; }
    setReports(result.data.reports);
  }

  useEffect(() => { void loadReports(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [auth?.session, filterStatus, filterMode, filterPriority, filterUserId, filterOrganizationId, filterStartDate, filterEndDate]);
  useEffect(() => {
    if (!auth?.session || !sb || !realPlatformOrgListAccess) {
      setKbRescopeOrgs([]);
      setKbRescopeOrgsError(null);
      setKbRescopeOrgsLoading(false);
      return;
    }
    let cancelled = false;
    setKbRescopeOrgsLoading(true); setKbRescopeOrgsError(null);
    void (async () => {
      try {
        const { data, error } = await sb.rpc("platform_list_orgs");
        if (cancelled) return;
        if (error) { setKbRescopeOrgsError(error.message || String(error)); setKbRescopeOrgs([]); return; }
        const rows = ((data as any[]) || []).map((r) => ({ id: String(r.id || ""), name: String(r.name || r.id || "").trim() || String(r.id) })).filter((r) => r.id && isUuid(r.id)).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
        setKbRescopeOrgs(rows);
      } finally { if (!cancelled) setKbRescopeOrgsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [auth?.session, sb, realPlatformOrgListAccess]);
  useEffect(() => {
    if (!auth?.session) return;
    const intent = readKbDevRescopeIntent();
    if (!intent || lastKbIntentOpenedAtRef.current === intent.openedAt) return;
    lastKbIntentOpenedAtRef.current = intent.openedAt;
    setKbRescopeIntent(intent); setKbRescopeSlug(intent.slug);
    const sc = intent.current.scope;
    setKbRescopeTargetScope(sc === "app" || sc === "manufacturer" || sc === "org" ? sc : "org");
    setKbRescopeTargetOrgId(intent.current.organization_id || "");
    setKbRescopeUuidOverride(""); setKbRescopeReviewOpen(false); setKbRescopeMsg(null); setKbRescopeDetail(null); setKbRescopeCollapsed(false);
    window.requestAnimationFrame(() => document.getElementById("kb-dev-rescope-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [auth?.session]);
  useEffect(() => {
    if (window.location.hash.replace(/^#/, "") !== "kb-dev-rescope-panel") return;
    setKbRescopeCollapsed(false);
    window.requestAnimationFrame(() => document.getElementById("kb-dev-rescope-panel")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, []);

  function startEdit(report: BugReport) { setEditingId(report.id); setEditStatus(report.status); setEditPriority(report.priority); setEditNotes(report.notes ?? ""); setEditResolutionSummary(report.resolution_summary ?? ""); setSaveError(null); }
  function cancelEdit() { setEditingId(null); setSaveError(null); }
  async function saveEdit(id: string) {
    const token = await getToken(); if (!token) return;
    setSaving(true); setSaveError(null);
    const result = await apiFetch("/api/bugs/update", "POST", token, { id, status: editStatus, priority: editPriority, notes: editNotes, resolution_summary: editResolutionSummary });
    setSaving(false);
    if (!result.ok) { setSaveError(result.error); return; }
    setEditingId(null);
    setReports((prev) => prev.map((r) => r.id === id ? { ...r, status: editStatus, priority: editPriority, notes: editNotes, resolution_summary: editResolutionSummary } : r));
  }
  async function quickUpdate(report: BugReport, patch: { status?: string; priority?: string }) {
    const token = await getToken(); if (!token) return;
    const result = await apiFetch("/api/bugs/update", "POST", token, { id: report.id, ...patch, resolution_summary: report.resolution_summary ?? "" });
    if (!result.ok) { setError(result.error); return; }
    setReports((prev) => prev.map((i) => i.id === report.id ? { ...i, ...patch } : i));
  }
  function openElevate(report: BugReport) { setElevatingReport(report); setElevateError(null); }
  function closeElevate() { if (!elevateBusy) { setElevatingReport(null); setElevateError(null); } }
  async function submitElevate(args: ElevateModalSubmitArgs) {
    if (!elevatingReport) return;
    const token = await getToken(); if (!token) return;
    setElevateBusy(true); setElevateError(null);
    const result = await apiFetch<{ ok: boolean; dev_note_id: string; trust_tier: string; review_status: string | null; bug_report_status: string }>(
      "/api/admin/action?action=bug-elevate-to-dev-note", "POST", token,
      { bugReportId: elevatingReport.id, body: args.body, reviewerNote: args.reviewerNote, placement: args.placement },
    );
    setElevateBusy(false);
    if (!result.ok) { setElevateError(result.error); return; }
    const newStatus = result.data?.bug_report_status ?? "in_progress";
    const elevatedId = elevatingReport.id;
    setReports((prev) => prev.map((r) => r.id === elevatedId ? { ...r, status: newStatus } : r));
    setElevatingReport(null);
  }

  const { modes, reporters, organizations } = buildReportLookups(reports);
  const kbOrgLabel = (id: string | null | undefined) => !id ? null : kbRescopeOrgs.find((o) => o.id === id)?.name ?? id;
  function resolveRescopeOrgIdForApi(): { ok: true; organizationId: string | null } | { ok: false; error: string } {
    const override = kbRescopeUuidOverride.trim();
    if (override) return isUuid(override) ? { ok: true, organizationId: override } : { ok: false, error: "UUID override must be a valid organization id." };
    if (kbRescopeTargetScope === "org") {
      const id = kbRescopeTargetOrgId.trim();
      return id ? { ok: true, organizationId: id } : { ok: false, error: "Select a target organization (or enter a UUID override)." };
    }
    const id = kbRescopeTargetOrgId.trim();
    return { ok: true, organizationId: id || null };
  }
  function openKbRescopeReview() {
    setKbRescopeMsg(null); setKbRescopeDetail(null);
    const slug = kbRescopeSlug.trim();
    if (!slug) { setKbRescopeMsg("Enter an article slug."); return; }
    const orgRes = resolveRescopeOrgIdForApi();
    if (!orgRes.ok) { setKbRescopeMsg(orgRes.error); return; }
    setKbRescopeReviewOpen(true);
    window.requestAnimationFrame(() => document.getElementById("kb-dev-rescope-review")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }
  async function applyKbRescope() {
    const token = await getToken();
    if (!token) return;
    const slug = kbRescopeSlug.trim();
    const orgRes = resolveRescopeOrgIdForApi();
    if (!slug || !orgRes.ok) return;
    setKbRescopeBusy(true); setKbRescopeMsg(null); setKbRescopeDetail(null);
    const result = await apiFetch<{ article?: { before: unknown; after: unknown; slug: string } }>("/api/kb/admin/rescopeArticle", "POST", token, { slug, scope: kbRescopeTargetScope, confirm: true, organizationId: orgRes.organizationId });
    setKbRescopeBusy(false);
    if (!result.ok) { setKbRescopeMsg(result.error); return; }
    clearKbDevRescopeIntent(); setKbRescopeIntent(null); setKbRescopeReviewOpen(false); setKbRescopeMsg(`Rescoped “${result.data.article?.slug ?? slug}”.`); setKbRescopeDetail(JSON.stringify({ before: result.data.article?.before, after: result.data.article?.after }, null, 2));
  }

  // Container/padding doctrine \u00a7K1+K3: no outer chrome on the mode panel.
  // The shell (LeadWorkspaceShell) owns mobile padding via its phone-card
  // (`p-6 sm:p-8` dropped at `lg:+`) and desktop padding via `lg:px-6 lg:py-5`.
  // The mode panel just lays out its SectionCard children with vertical
  // rhythm. See docs/planning/container_padding_pass.md \u00a7K.
  return (
    <div className="w-full text-left space-y-4">
      <DevPanelActionableNotesSection getToken={getToken} />
      <DevInboundReceiptsPanel />
      <DevActivityForensicsPanel />
      <DevPanelBugReportsSection {...{
        reports, loading, error, filterStatus, setFilterStatus, filterMode, setFilterMode, filterPriority, setFilterPriority,
        filterUserId, setFilterUserId, filterOrganizationId, setFilterOrganizationId, filterStartDate, setFilterStartDate,
        filterEndDate, setFilterEndDate, modes, reporters, organizations, loadReports, editingId, editStatus, setEditStatus,
        editPriority, setEditPriority, expandedId, setExpandedId, quickUpdate, editNotes, setEditNotes, editResolutionSummary,
        setEditResolutionSummary, saveError, saving, saveEdit, cancelEdit, startEdit,
        onElevate: canElevateBugs ? openElevate : undefined,
      }} />
      {elevatingReport ? (
        <DevPanelElevateBugModal
          report={elevatingReport}
          busy={elevateBusy}
          error={elevateError}
          onClose={closeElevate}
          onSubmit={submitElevate}
        />
      ) : null}
      <DevPanelKbRescopeSection {...{
        kbRescopeCollapsed, setKbRescopeCollapsed, kbRescopeOrgsError, kbRescopeOrgsLoading, kbRescopeSlug, setKbRescopeSlug,
        kbRescopeTargetScope, setKbRescopeTargetScope, kbRescopeTargetOrgId, setKbRescopeTargetOrgId, kbRescopeOrgs,
        kbRescopeUuidOverride, setKbRescopeUuidOverride, kbRescopeBusy, openKbRescopeReview, kbRescopeReviewOpen,
        setKbRescopeReviewOpen, kbRescopeIntent, resolveRescopeOrgIdForApi, kbOrgLabel, applyKbRescope, kbRescopeMsg, kbRescopeDetail,
      }} />
      <DevPanelActivityDockPeekStatsSection />
      <DevPanelFireflyTargetHighlightSection />
      <DevPanelFireflyLabSection />
    </div>
  );
}

